import { noItx } from "../../../src/translationItx/itx";
import { useEffect, useMemo, useState } from "react";

const processedAssets = new Map<string, Promise<string>>();

const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

export function contrastFactor(percent: number) {
  return Math.min(1.8, Math.max(1, percent / 100));
}

function adjustedColor(value: string, factor: number) {
  const hex = value.trim().match(/^#([\da-f]{3}|[\da-f]{6})$/i);
  const rgb = value.trim().match(/^rgba?\(\s*([\d.]+)[, ]+\s*([\d.]+)[, ]+\s*([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/i);
  let channels: number[] | undefined;
  let alpha: string | undefined;
  if (hex) {
    const raw = hex[1].length === 3 ? [...hex[1]].map((part) => part + part).join("") : hex[1];
    channels = [0, 2, 4].map((offset) => Number.parseInt(raw.slice(offset, offset + 2), 16));
  } else if (rgb) {
    channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    alpha = rgb[4];
  }
  if (!channels) return value;
  const adjusted = channels.map((channel) => clampByte(255 - (255 - channel) * factor));
  return alpha === undefined ? `rgb(${adjusted.join(", ")})` : `rgba(${adjusted.join(", ")}, ${alpha})`;
}

export function applyInlineEloGraphicContrast(svg: SVGSVGElement) {
  svg.querySelectorAll<SVGElement>("[data-c1-elo-vector-contrast]").forEach((root) => {
    const factor = contrastFactor(Number(root.dataset.c1EloVectorContrast));
    root.style.removeProperty("filter");
    root.removeAttribute("data-c1-elo-vector-contrast");
    [root, ...root.querySelectorAll<SVGElement>("*")].forEach((element) => {
      for (const property of ["fill", "stroke", "color", "stop-color", "flood-color"] as const) {
        const styleValue = element.style.getPropertyValue(property);
        if (styleValue) element.style.setProperty(property, adjustedColor(styleValue, factor));
        const attributeValue = element.getAttribute(property);
        if (attributeValue) element.setAttribute(property, adjustedColor(attributeValue, factor));
      }
    });
  });
}

function svgWithContrast(source: string, factor: number) {
  const document = new DOMParser().parseFromString(source, "image/svg+xml");
  const svg = document.documentElement;
  if (svg.localName !== "svg" || document.querySelector("parsererror")) throw new Error("UngÃ¼ltige SVG-Grafik");
  [svg, ...svg.querySelectorAll<SVGElement>("*")].forEach((element) => {
    for (const property of ["fill", "stroke", "color", "stop-color", "flood-color"] as const) {
      const styleValue = element.style.getPropertyValue(property);
      if (styleValue) element.style.setProperty(property, adjustedColor(styleValue, factor));
      const attributeValue = element.getAttribute(property);
      if (attributeValue) element.setAttribute(property, adjustedColor(attributeValue, factor));
    }
  });
  return new XMLSerializer().serializeToString(document);
}

async function pngWithContrast(blob: Blob, factor: number) {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas-Kontext fÃ¼r ELO-Grafik fehlt");
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < image.data.length; index += 4) {
    image.data[index] = clampByte(255 - (255 - image.data[index]) * factor);
    image.data[index + 1] = clampByte(255 - (255 - image.data[index + 1]) * factor);
    image.data[index + 2] = clampByte(255 - (255 - image.data[index + 2]) * factor);
  }
  context.putImageData(image, 0, 0);
  const result = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((value) => (value ? resolve(value) : reject(new Error("PNG-Konvertierung fehlgeschlagen"))), "image/png"),
  );
  return URL.createObjectURL(result);
}

export function processedEloGraphicUrl(sourceUrl: string, contrastPercent: number) {
  const factor = contrastFactor(contrastPercent);
  if (factor === 1) return Promise.resolve(sourceUrl);
  const key = `${sourceUrl}|${contrastPercent}|v3`;
  let pending = processedAssets.get(key);
  if (!pending) {
    pending = fetch(sourceUrl).then(async (response) => {
      if (!response.ok) throw new Error(`ELO-Grafik konnte nicht geladen werden: ${sourceUrl}`);
      const blob = await response.blob();
      const svg = blob.type.includes("svg") || /\.svg(?:$|[?#])/i.test(sourceUrl);
      if (!svg) return pngWithContrast(blob, factor);
      const transformed = svgWithContrast(await blob.text(), factor);
      return URL.createObjectURL(new Blob([transformed], { type: "image/svg+xml" }));
    });
    processedAssets.set(key, pending);
  }
  return pending;
}

export function useProcessedEloGraphicUrls(sourceUrls: readonly string[], contrastPercent: number) {
  const key = sourceUrls.join("|");
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const uniqueUrls = useMemo(() => [...new Set(sourceUrls)], [key]);
  useEffect(() => {
    let active = true;
    void Promise.all(uniqueUrls.map(async (url) => [url, await processedEloGraphicUrl(url, contrastPercent)] as const))
      .then((entries) => {
        if (active) setUrls(new Map(entries));
      })
      .catch((error) => console.error(noItx("Could not adjust electrical graphic contrast."), error));
    return () => {
      active = false;
    };
  }, [key, contrastPercent]);
  return urls;
}
